import { getConfig, getConfigAsync } from '../utils/config';
import { logger } from '../utils/logger';

export interface KlineData {
  time: number; // Unix timestamp in seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface BackendMessage {
  type: 'kline_update' | 'historical_data' | 'pong' | 'status' | 'config_updated' | 'server_shutdown' | 'data_updated';
  symbol?: string;
  interval?: string;
  data?: KlineData | KlineData[];
  timestamp?: number;
  is_closed?: boolean;
  config?: any;
  latest_data?: any;
  client_count?: number;
  binance_connected?: boolean;
  message?: string;
  kline?: KlineData;
}

export class BackendWebSocketService {
  private ws: WebSocket | null = null;
  private onKlineUpdate: (kline: KlineData) => void;
  private onConnectionStatusChange?: (status: 'connected' | 'disconnected' | 'reconnecting') => void;
  private onError?: (error: string) => void;
  private onDataUpdated?: () => void;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // 1 second
  private pingInterval: NodeJS.Timeout | null = null;
  private connectionStatus: 'connected' | 'disconnected' | 'reconnecting' = 'disconnected';
  private wsUrl: string;
  private expectedSymbol?: string;
  private expectedInterval?: string;

  constructor(
    onKlineUpdate: (kline: KlineData) => void,
    onConnectionStatusChange?: (status: 'connected' | 'disconnected' | 'reconnecting') => void,
    onError?: (error: string) => void,
    onDataUpdated?: () => void,
    expectedSymbol?: string,
    expectedInterval?: string
  ) {
    this.onKlineUpdate = onKlineUpdate;
    this.onConnectionStatusChange = onConnectionStatusChange;
    this.onError = onError;
    this.onDataUpdated = onDataUpdated;

    // 从配置获取WebSocket URL
    const config = getConfig();
    this.wsUrl = process.env.REACT_APP_WS_URL || config.WS_URL || 'wss://getaws.alpha-pilot.net/ws/';
    logger.log('Backend WebSocket URL:', this.wsUrl);
    this.expectedSymbol = expectedSymbol;
    this.expectedInterval = expectedInterval;
  }

  connect() {
    try {
      logger.log(`🔗 Connecting to backend WebSocket: ${this.wsUrl}`);

      this.setConnectionStatus('reconnecting');
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        logger.log('Backend WebSocket connected');
        this.reconnectAttempts = 0;
        this.setConnectionStatus('connected');

        // 请求当前状态
        this.requestStatus();

        // 启动心跳
        this.startPing();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: BackendMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          logger.error('Error parsing backend WebSocket message:', error);
          this.onError?.('Failed to parse server message');
        }
      };

      this.ws.onclose = (event) => {
        logger.log('🔌 Backend WebSocket disconnected', event.code, event.reason);
        this.setConnectionStatus('disconnected');
        this.stopPing();

        // 如果不是手动关闭，尝试重连
        if (event.code !== 1000) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        logger.error('Backend WebSocket error:', error);
        this.onError?.('WebSocket connection error');
      };
    } catch (error) {
      logger.error('Failed to connect to backend WebSocket:', error);
      this.onError?.('Failed to connect to server');
      this.attemptReconnect();
    }
  }

  private setConnectionStatus(status: 'connected' | 'disconnected' | 'reconnecting') {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      this.onConnectionStatusChange?.(status);
    }
  }

  private handleMessage(message: BackendMessage) {
    switch (message.type) {
      case 'kline_update':
        if (message.data && !Array.isArray(message.data)) {
          // 仅处理与期望 symbol/interval 匹配的更新
          const msgSymbol = (message.symbol || '').toUpperCase();
          const msgInterval = (message.interval || '').toLowerCase();

          if (this.expectedSymbol && msgSymbol && msgSymbol !== this.expectedSymbol.toUpperCase()) {
            return;
          }
          if (this.expectedInterval && msgInterval && msgInterval !== this.expectedInterval.toLowerCase()) {
            return;
          }

          this.onKlineUpdate(message.data);
        }
        break;

      case 'historical_data':
        if (message.data && Array.isArray(message.data)) {
          // 历史数据处理可以在这里扩展
          logger.log(`Received ${message.data.length} historical klines`);
        }
        break;

      case 'pong':
        // 心跳响应，保持连接活跃
        logger.debug('Pong received');
        break;

      case 'status':
        // 服务器状态更新
        logger.log('Server status:', {
          config: message.config,
          binance_connected: message.binance_connected,
          client_count: message.client_count
        });

        // 如果有最新数据，发送给回调
        if (message.latest_data?.data) {
          this.onKlineUpdate(message.latest_data.data);
        }
        break;

      case 'data_updated':
        // 本地数据文件已更新，触发重新加载
        logger.log('📝 Local data updated, triggering reload:', message.message);
        if (message.kline) {
          logger.log('New completed kline:', message.kline);
        }
        // Broadcast a global browser event so other UI (e.g., notifications) can refresh immediately
        try {
          window.dispatchEvent(new CustomEvent('backend_data_updated', { detail: message }));
        } catch {
          // ignore
        }
        this.onDataUpdated?.();
        break;

      case 'config_updated':
        logger.log('Server config updated:', message.config);
        break;

      case 'server_shutdown':
        logger.warn('Server is shutting down:', message.message);
        this.onError?.('Server is shutting down');
        this.setConnectionStatus('disconnected');
        break;

      default:
        logger.warn('Unknown message type:', message.type);
    }
  }

  private startPing() {
    // 每30秒发送一次心跳
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        this.send({
          type: 'ping',
          timestamp: Date.now()
        });
      }
    }, 30000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private send(data: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  requestStatus() {
    if (this.isConnected()) {
      this.send({
        type: 'get_status'
      });
    }
  }

  requestHistoricalData(symbol?: string, interval?: string, limit: number = 100) {
    if (this.isConnected()) {
      this.send({
        type: 'get_historical',
        symbol: symbol || 'BTCUSDT',
        interval: interval || '1h',
        limit: limit
      });
    }
  }

  setExpected(symbol: string, interval: string) {
    this.expectedSymbol = symbol;
    this.expectedInterval = interval;
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached');
      this.onError?.('Unable to connect to server after multiple attempts');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000); // 指数退避，最大30秒

    logger.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
    this.setConnectionStatus('reconnecting');

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect() {
    logger.log('Manually disconnecting from backend WebSocket');

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPing();

    if (this.ws) {
      // 设置一个标志，防止自动重连
      this.reconnectAttempts = this.maxReconnectAttempts;
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }

    this.setConnectionStatus('disconnected');
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN && this.connectionStatus === 'connected';
  }

  getConnectionStatus(): 'connected' | 'disconnected' | 'reconnecting' {
    return this.connectionStatus;
  }
}

// 静态方法：获取历史数据（使用本地数据，不再调用后端API）
export interface HistoricalKlinePage {
  data: KlineData[];
  nextCursor: string | null;
  hasMore: boolean;
}

export async function fetchHistoricalFromBackend(
  symbol: string = 'BTCUSDT',
  interval: string = '1h',
  limit: number = 5000,
  cursor?: string,
  direction: 'desc' | 'asc' = 'desc'
): Promise<HistoricalKlinePage> {
  try {
    const config = await getConfigAsync();
    const backendUrl = config.API_BASE_URL || 'https://getaws.alpha-pilot.net';
    const params = new URLSearchParams({
      symbol,
      interval,
      limit: String(limit),
      direction,
    });
    if (cursor) {
      params.set('cursor', cursor);
    }
    const url = `${backendUrl.replace(/\/$/, '')}/api/klines?${params.toString()}`;
    const { getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
    const response = await fetch(url, {
      headers: getApiHeadersWithoutContentType()
    });
    if (!response.ok) {
      logger.warn(`Backend API responded ${response.status}`);
      return { data: [], nextCursor: null, hasMore: false };
    }
    const json = await response.json();
    if (json && json.success && Array.isArray(json.data)) {
      const data = json.data.map((item: any) => ({
        time: Number(item.time),
        open: Number(item.open),
        high: Number(item.high),
        low: Number(item.low),
        close: Number(item.close),
        volume: Number(item.volume || 0),
      }));
      return {
        data,
        nextCursor: json.next_cursor ?? null,
        hasMore: Boolean(json.has_more),
      };
    }
    return { data: [], nextCursor: null, hasMore: false };
  } catch (error) {
    logger.error('Failed to fetch historical from backend:', error);
    return { data: [], nextCursor: null, hasMore: false };
  }
}

// 获取可用的时间框架
export async function fetchAvailableTimeframes(
  symbol: string = 'BTCUSDT'
): Promise<{ interval: string; normalized_interval: string; count: number; available: boolean }[]> {
  try {
    const config = await getConfigAsync();
    const backendUrl = config.API_BASE_URL || 'https://getaws.alpha-pilot.net';
    const url = `${backendUrl.replace(/\/$/, '')}/api/timeframes?symbol=${encodeURIComponent(symbol)}`;
    const { getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
    const response = await fetch(url, {
      headers: getApiHeadersWithoutContentType()
    });
    if (!response.ok) {
      logger.warn(`Backend /api/timeframes responded ${response.status}`);
      return [];
    }
    const json = await response.json();
    if (json && json.success && Array.isArray(json.available_timeframes)) {
      return json.available_timeframes;
    }
    return [];
  } catch (error) {
    logger.error('Failed to fetch available timeframes:', error);
    return [];
  }
}

// 获取多时间框架数据
export async function fetchMultiTimeframeData(
  symbol: string = 'BTCUSDT',
  intervals: string[] = ['1h', '1m'],
  limit: number = 10000
): Promise<{ [interval: string]: { data: KlineData[]; count: number; error?: string } }> {
  try {
    const config = await getConfigAsync();
    const backendUrl = config.API_BASE_URL || 'https://getaws.alpha-pilot.net';
    const intervalsParam = intervals.map(i => `intervals=${encodeURIComponent(i)}`).join('&');
    const url = `${backendUrl.replace(/\/$/, '')}/api/klines/multi-timeframe?symbol=${encodeURIComponent(symbol)}&${intervalsParam}&limit=${encodeURIComponent(String(limit))}`;
    const { getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
    const response = await fetch(url, {
      headers: getApiHeadersWithoutContentType()
    });
    if (!response.ok) {
      logger.warn(`Backend multi-timeframe API responded ${response.status}`);
      return {};
    }
    const json = await response.json();
    if (json && json.success && json.timeframes) {
      const result: { [interval: string]: { data: KlineData[]; count: number; error?: string } } = {};
      Object.keys(json.timeframes).forEach(interval => {
        const timeframe = json.timeframes[interval];
        result[interval] = {
          data: timeframe.data ? timeframe.data.map((item: any) => ({
            time: Number(item.time),
            open: Number(item.open),
            high: Number(item.high),
            low: Number(item.low),
            close: Number(item.close),
            volume: Number(item.volume || 0),
          })) : [],
          count: timeframe.count || 0,
          error: timeframe.error
        };
      });
      return result;
    }
    return {};
  } catch (error) {
    logger.error('Failed to fetch multi-timeframe data:', error);
    return {};
  }
}

// WebSocket服务API控制函数
export async function controlWebSocketService(action: 'start' | 'stop' | 'restart' | 'status' | 'health') {
  try {
    const config = await getConfigAsync();
    const backendUrl = config.API_BASE_URL || 'https://getaws.alpha-pilot.net';
    let url = `${backendUrl.replace(/\/$/, '')}/api/websocket/${action}`;

    // MVP模式使用专门的启动端点
    if (action === 'start') {
      url = `${backendUrl.replace(/\/$/, '')}/api/websocket/mvp/start`;
    }

    const method = action === 'status' || action === 'health' ? 'GET' : 'POST';
    const { getApiHeaders, getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
    const headers = method === 'GET' ? getApiHeadersWithoutContentType() : getApiHeaders();

    const response = await fetch(url, {
      method,
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    logger.error(`Failed to ${action} WebSocket service:`, error);
    throw error;
  }
}
